import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './About.css';

const team = [
  { name: 'Dr. Arjun Mehta', role: 'Chief Medical Officer', bio: '20+ years in transfusion medicine and hematology. Former HOD at AIIMS Chennai.' },
  { name: 'Priya Krishnan', role: 'Operations Director', bio: 'Built donor networks across 14 states. Passionate about accessible healthcare.' },
  { name: 'Rajan Subramaniam', role: 'Technology Lead', bio: 'Designed our secure donor database system used by 50+ blood banks.' },
  { name: 'Dr. Sunita Patel', role: 'Donor Relations', bio: 'Trained counselor helping donors understand the impact of their contribution.' }
];

const milestones = [
  { year: '2018', event: 'BloodBridge founded in Salem, Tamil Nadu with 40 volunteers' },
  { year: '2019', event: 'Expanded to 5 districts, 200 registered donors' },
  { year: '2020', event: 'Emergency response during COVID-19 — 600 critical donations' },
  { year: '2021', event: 'Launched digital donor management system' },
  { year: '2022', event: 'Partnered with 30+ hospitals across Tamil Nadu' },
  { year: '2023', event: 'Crossed 1,000 registered donors milestone' },
  { year: '2024', event: '3,600+ lives saved. Expanding to Karnataka & Andhra Pradesh' }
];

const values = [
  { icon: '♦', title: 'Accessibility', desc: 'Blood donation should be easy for both donors and recipients. We remove every barrier we can.' },
  { icon: '◈', title: 'Trust', desc: 'Donor data is private, secure, and used only for life-saving purposes. No exceptions.' },
  { icon: '◉', title: 'Urgency', desc: 'We treat every request as urgent. Someone\'s life depends on our speed and accuracy.' },
  { icon: '◇', title: 'Community', desc: 'We build networks of care across neighborhoods, cities, and states.' }
];

const About = () => {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    reveals.forEach(r => observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>About BloodBridge — Our Mission to Save Lives</title>
        <meta name="description" content="Learn about BloodBridge — founded in Salem, Tamil Nadu in 2018. Our mission, team, and journey to connect blood donors with patients in need across India." />
        <link rel="canonical" href="https://blood.msdiscoveries.com/about" />
      </Helmet>

      {/* ── PAGE HEADER ── */}
      <div className="page-header about-header">
        <div className="container">
          <div className="section-label reveal" style={{ justifyContent: 'center' }}>Our Story</div>
          <h1 className="reveal">Built on the Belief<br /><em>Life is Sacred.</em></h1>
          <p className="reveal">
            BloodBridge was founded in 2018 in Salem, Tamil Nadu by a group of 
            doctors and volunteers who believed no one should die waiting for blood. 
            Six years later, we've saved over 3,600 lives.
          </p>
        </div>
      </div>

      {/* ── MISSION ── */}
      <section className="section section-light">
        <div className="container">
          <div className="mission-grid">
            <div className="reveal">
              <div className="section-label">Our Mission</div>
              <h2 className="mission-heading">Connecting donors.<br />Saving lives.</h2>
              <p>
                BloodBridge exists to eliminate the gap between blood donors and 
                those in urgent medical need. We maintain a verified, searchable 
                database of willing donors across all blood groups — available 
                around the clock for hospitals, families, and emergency responders.
              </p>
              <p>
                We believe that the right donor, found quickly, is the difference 
                between life and death. Our platform makes that connection instant, 
                reliable, and free for everyone involved.
              </p>
            </div>
            <div className="mission-visual reveal reveal-delay-2">
              <div className="mission-art">
                <div className="ma-outer">
                  <div className="ma-inner">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" width="80" height="80" className="animate-heartbeat">
                      <path d="M12 2C12 2 5 9.5 5 14a7 7 0 0 0 14 0C19 9.5 12 2 12 2z"/>
                    </svg>
                    <div className="ma-label">Every Drop Counts</div>
                  </div>
                  <div className="ma-ring ma-ring-1" />
                  <div className="ma-ring ma-ring-2" />
                  <div className="ma-ring ma-ring-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-label reveal">What Drives Us</div>
          <h2 className="values-title reveal">Our Core Values</h2>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={i} className={`value-card reveal reveal-delay-${i}`}>
                <span className="value-icon">{v.icon}</span>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="section">
        <div className="container">
          <div className="section-label reveal">Our Journey</div>
          <h2 className="timeline-title reveal">Six Years of Impact</h2>
          <div className="timeline">
            {milestones.map((m, i) => (
              <div key={i} className={`timeline-item reveal reveal-delay-${i % 4}`}>
                <div className="timeline-year">{m.year}</div>
                <div className="timeline-connector">
                  <div className="timeline-dot" />
                  <div className="timeline-line" />
                </div>
                <div className="timeline-event">{m.event}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-label reveal">The People</div>
          <h2 className="team-title reveal">Meet Our Team</h2>
          <div className="team-grid">
            {team.map((m, i) => (
              <div key={i} className={`team-card reveal reveal-delay-${i % 4}`}>
                <div className="team-avatar">
                  <span className="team-initials">
                    {m.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </span>
                </div>
                <h3 className="team-name">{m.name}</h3>
                <span className="team-role">{m.role}</span>
                <p className="team-bio">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section about-cta">
        <div className="container">
          <div className="about-cta-inner reveal">
            <h2>Join Our Mission</h2>
            <p>
              Whether you donate blood, spread awareness, or volunteer your time — 
              every form of support saves lives.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn btn-primary">Get Involved</Link>
              <Link to="/" className="btn btn-outline">View Donors</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
